import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "クラウドサイン Client ID 登録",
  updatedAt: "2026-07-23",
};

export function Content() {
  return (
    <>
      <p>
        書類送信にはクラウドサイン側で発行された <strong>Client ID</strong> が必要です。
        当サービスは複数の Client ID を登録でき、送信時に切り替えられます
        （部署別・送信者別の運用に対応）。
      </p>

      <h2>セットアップ手順の全体像</h2>
      <p>
        ダッシュボードの「<strong>接続設定</strong>」タブにある
        「セットアップ手順」の通り、5 ステップで完了します:
      </p>
      <ol>
        <li><strong>Web API を有効化</strong>: クラウドサイン管理画面 → チーム設定 → Web API →「利用する」</li>
        <li><strong>クライアント ID を発行</strong>: 送信者ごとにクラウドサイン管理画面で発行</li>
        <li><strong>クライアントID一覧から登録</strong>: 「+ 新規追加」で当サービスに登録（複数登録可能）</li>
        <li><strong>接続テストで疎通確認</strong>: 各クライアント ID ごとにテスト可能</li>
        <li><strong>設定を確認</strong>: 「デフォルト設定」で格納先プロパティ・送信モード・宛先などを設定</li>
      </ol>
      <ScreenshotBox src="/guide/screenshots/cs-clientid-1.png" caption="接続設定タブの「セットアップ手順」セクション" />

      <Callout type="warning" title="クラウドサインのプラン要件">
        Web API を利用するには、クラウドサインの <strong>スタンダードプラン以上</strong>のご契約が必要です。
      </Callout>

      <h2>STEP 1: Web API の有効化</h2>
      <p>
        クラウドサイン管理画面にログインし、「<strong>チーム設定</strong>」→「<strong>Web API</strong>」
        を開きます。「利用する」を ON にすると、Client ID の発行が可能になります。
      </p>
      <p>
        詳しい手順はクラウドサインのヘルプサイトをご参照ください。
      </p>

      <h2>STEP 2: Client ID の発行</h2>
      <p>
        クラウドサインの管理画面で、送信者（送信を行うクラウドサインユーザー）ごとに
        Client ID を発行します。複数の送信者を運用する場合は、それぞれ別の Client ID を
        発行してください。
      </p>

      <h2>STEP 3: 当サービスに登録</h2>
      <p>
        ダッシュボードの「接続設定」タブを開き、「クライアントID一覧」セクションの
        「<strong>+ 新規追加</strong>」ボタンをクリックします。
      </p>
      <ScreenshotBox src="/guide/screenshots/cs-clientid-2.png" caption="クライアントID 一覧と新規追加ボタン" />

      <h3>入力項目</h3>
      <ul>
        <li>
          <strong>ラベル</strong>（必須）: 社内識別用の名称。例:「営業部」「法務担当」「経理 田中」など。
          送信モーダルで送信者を選ぶときに表示される
        </li>
        <li>
          <strong>Client ID</strong>（必須）: クラウドサインで発行された文字列。
          保存時に暗号化されるため、画面上で再表示することはできません
        </li>
        <li>
          <strong>環境</strong>（必須）:
          <ul style={{ margin: "4px 0" }}>
            <li><strong>本番</strong>: 通常の運用。実際の電子署名が法的に有効</li>
            <li><strong>sandbox</strong>: クラウドサインのテスト環境。動作確認用</li>
          </ul>
        </li>
        <li>
          <strong>デフォルト送信者に設定する</strong>: チェックを入れると、送信モーダルで初期選択される送信者になります。
          既定はテナント全体で 1 つです（環境ごとに別々には持てません）
        </li>
      </ul>

      <h2>STEP 4: 接続テスト</h2>
      <p>
        登録後、各 Client ID の「<strong>接続テスト</strong>」ボタンで動作確認できます。
        成功時は「接続成功」、失敗時はエラーメッセージが表示されます。
      </p>

      <h2>STEP 5: 送信設定を確認</h2>
      <p>
        「クライアントID一覧」の先頭にある「<strong>デフォルト設定</strong>」の
        「編集」から、送信モード・書類の転送設定・受信者氏名の敬称を設定します。
        ここで設定した内容が、個別設定を持たないすべての Client ID に適用されます。
      </p>
      <p>
        Client ID ごとに違う設定にしたい場合は、各 Client ID の
        「<strong>送信設定</strong>」ボタンから上書きできます。詳しくは{" "}
        <a href="/guide/clientid-mgmt">Client ID 管理（複数送信者）</a> を参照してください。
      </p>

      <Callout type="warning" title="環境の選び間違いに注意">
        本番用に発行された Client ID を「sandbox」として登録すると、送信した書類が
        本番のクラウドサインアカウントに反映されません。逆も同様です。
        Client ID 発行時に「本番用 / sandbox 用」を必ず確認してください。
      </Callout>

      <h2>削除</h2>
      <p>
        登録済みの Client ID は同じタブから削除できます（ラベルや Client ID の
        編集機能はないため、変更したい場合は削除して登録し直してください）。ただし、
        その Client ID で過去に送信した書類のステータス同期は、削除すると
        以降停止します（対象書類は「取得不可」として扱われます）。
      </p>

      <Callout type="tip" title="複数 Client ID の使い方">
        複数登録した場合、送信モーダルで送信者を選択できます。
        詳しくは <a href="/guide/clientid-mgmt">Client ID 管理（複数送信者）</a> を参照してください。
      </Callout>
    </>
  );
}
